// Lists screens. Pure: data in, HTML out.
// Words: LISTS_PAGE_WORDS.md. Mockup: MOCKUP_LISTS_APPROVED.html (approved 2026-10-01).
// Reorder: Order, then drag the handle, then Done (LISTS_DRAG_B3_WORDS.md, 2026-10-28). It was Move Up and Move Down.
// Every change goes through server code (functions/src/lists.ts).

import type { ListsData } from "./firebase";
import {
  FIELD_LIST_ORDER,
  groupValues,
  groupsOf,
  listSummaries,
  slug,
  sortClasses,
  sortPlants,
  type ClassInput,
  type EquipmentClass,
  type FieldValue,
  type FieldValueInput,
  type Plant,
  type PlantInput,
} from "./lists";
import { esc, ident } from "./training-screens";
import { PREFIX_LIST } from "./model-tag-prefixes";

function row(label: string, value: string, end = ""): string {
  return `<div class="r"><div class="k">${esc(label)}</div><div class="v">${value}</div><div class="e">${end}</div></div>`;
}

function sheet(title: string, body: string): string {
  return `<div class="shade"><div class="sheet" role="dialog" aria-modal="true" aria-label="${esc(title)}">
    <div class="big">${esc(title)}</div>${body}</div></div>`;
}

const NONE = '<span class="muted">None</span>';
const txt = (v: unknown) => (v === "" || v === null || v === undefined ? NONE : esc(v));
const listTxt = (v: string[]) => (v.length ? esc(v.join(", ")) : NONE);
const pill = (on: boolean) => `<span class="pill ${on ? "green" : "grey"}">${on ? "ACTIVE" : "OFF"}</span>`;

// ---- 1. Lists home ----

export function listsMenu(d: ListsData): string {
  const lists = listSummaries(d.values, FIELD_LIST_ORDER);
  const r = (href: string, code: string, title: string, line: string) =>
    `<a class="menu-row" href="#/lists/${href}"><span class="num">${code}</span><span class="t"><b>${esc(title)}</b><small>${esc(line)}</small></span><span class="chev">›</span></a>`;
  return `${ident("LS", "Lists", "What the IEA Site Survey offers, and the plants not yet sites")}
    <div class="box">
      ${r("classes", "EC", "Equipment Classes", `${d.classes.length} classes · ${d.classes.filter((c) => c.isActive).length} active`)}
      ${r("fields", "FL", "Field Lists", `${lists.length} lists · ${d.values.length} values`)}
      ${r("plants", "PL", "Plants", `${d.plants.length} plants not yet sites`)}
    </div>
    <div class="note green">Equipment Classes and Field Lists feed the IEA Site Survey. Plants feed Add Site on Home.</div>`;
}

// ---- Order by drag and drop (B3) ----

/** Order mode on a list: the order so far by group ("" for classes or an ungrouped list), changes, and a save error. */
export interface ListOrderView {
  ids: Record<string, string[]>;
  moves: number;
  error: string;
  busy: boolean;
}

const GRIP = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" fill="none"/></svg>`;
const handle = (name: string): string => `<button class="ls-handle" type="button" aria-label="Hold and drag ${esc(name)}">${GRIP}</button>`;

/** Order, or Cancel and Done while ordering. */
function orderButtons(o: ListOrderView | undefined): string {
  return o
    ? `<span class="ls-obtns"><button class="ai-tbtn" type="button" data-act="list-order-cancel"${o.busy ? " disabled" : ""}>Cancel</button><button class="ai-tbtn dark" type="button" data-act="list-order-done"${o.busy ? " disabled" : ""}>${o.busy ? "Saving" : "Done"}</button></span>`
    : `<button class="chg" type="button" data-act="list-order">Order</button>`;
}

function orderLines(o: ListOrderView | undefined): string {
  if (!o) return "";
  const n = o.moves;
  return `${o.error ? `<div class="note red">${esc(o.error)}</div>` : ""}${
    n ? `<div class="ls-chgline">${n} ${n === 1 ? "change" : "changes"} since you tapped Order. Cancel puts ${n === 1 ? "it" : "them"} back. Done keeps ${n === 1 ? "it" : "them"}.</div>` : `<div class="ls-chgline">Hold ≡ and drag. Done saves the order.</div>`
  }`;
}

/** Puts items in the order being dragged; anything not in it keeps its place at the end. */
function inOrder<T>(list: T[], idOf: (x: T) => string, ids: string[] | undefined): T[] {
  if (!ids) return list;
  const by = new Map(list.map((x) => [idOf(x), x]));
  const out = ids.map((id) => by.get(id)).filter((x): x is T => !!x);
  return [...out, ...list.filter((x) => !ids.includes(idOf(x)))];
}

// ---- 2 to 4. Equipment classes ----

export function classList(d: ListsData, flash = "", order?: ListOrderView): string {
  if (order) {
    const list = inOrder(sortClasses(d.classes), (c) => c.code, order.ids[""]);
    const rows = list
      .map(
        (c, i) => `<div class="menu-row${c.isActive ? "" : " dim"}" data-drag-item="${esc(c.code)}">${handle(c.name)}
        <span class="num">${String(i + 1).padStart(2, "0")}</span>
        <span class="t"><b>${esc(c.name)}</b><small class="mono">${esc(c.code)}</small></span>${pill(c.isActive)}</div>`,
      )
      .join("");
    return `<a class="back" href="#/lists">Lists</a>
    ${ident("EC", "Equipment Classes", `${d.classes.length} classes · ${d.classes.filter((c) => c.isActive).length} active · in IEA order`)}
    <div class="pb-actions">${orderButtons(order)}</div>
    ${orderLines(order)}
    <div class="box">${rows}</div>`;
  }
  const rows = sortClasses(d.classes)
    .map(
      (c) => `<a class="menu-row${c.isActive ? "" : " dim"}" href="#/lists/classes/${encodeURIComponent(c.code)}">
        <span class="num">${c.order === null ? "--" : String(c.order).padStart(2, "0")}</span>
        <span class="t"><b>${esc(c.name)}</b><small class="mono">${esc(c.code)}</small></span>${pill(c.isActive)}</a>`,
    )
    .join("");
  return `<a class="back" href="#/lists">Lists</a>
    ${ident("EC", "Equipment Classes", `${d.classes.length} classes · ${d.classes.filter((c) => c.isActive).length} active · in IEA order`)}
    ${flash}
    <div class="pb-actions">${orderButtons(undefined)}</div>
    <div class="box">${rows}</div>
    <p class="ro">Adding a class is parked. It needs a card type and device rules.</p>`;
}

export function classDetail(c: EquipmentClass, _sortedCodes: string[], flash = ""): string {
  const id = encodeURIComponent(c.code);
  return `<a class="back" href="#/lists/classes">Equipment Classes</a>
    ${ident(c.code.slice(0, 3), c.name, `Equipment class ${c.code}`)}
    ${flash}
    <div class="box">
      <div class="boxhd"><div class="nm"><b>${esc(c.name)}</b><small>The IEA Site Survey uses these the next time it loads.</small></div><a class="chg" href="#/lists/classes/${id}/edit">Change</a></div>
      <div class="rows">
        ${row("Code", `<span class="chip">${esc(c.code)}</span>`, '<span class="lock">Locked</span>')}
        ${row("Order", c.order === null ? NONE : String(c.order), '<a class="lock" href="#/lists/classes">Order on the list</a>')}
        ${row("NEC 110.16 Label", c.nec11016Label ? "Yes" : "No")}
        ${row("IEEE Class", txt(c.ieeeClass))}
        ${row("Card", txt(c.card))}
        ${row("Rating", txt(c.rating))}
        ${row("Devices", listTxt(c.devices))}
        ${row("Position Type", txt(c.positionType))}
        ${row("Also Accepts", listTxt(c.alsoAccepts))}
        ${row("Notes", txt(c.notes))}
        ${row("Active", `${pill(c.isActive)} <small class="muted">${c.isActive ? "Shows as a choice in the IEA Site Survey." : "Hidden as a choice. Assets that use it keep it."}</small>`, `<button class="chg" type="button" data-act="item-active" data-kind="class" data-id="${esc(c.code)}">${c.isActive ? "Turn Off" : "Turn On"}</button>`)}
      </div>
    </div>
    <div class="note">Code is locked. It is part of Asset IDs already in use, like 01-ER1-${esc(c.code)}-01.</div>`;
}

export function classForm(c: EquipmentClass, entry: ClassInput | null, error = ""): string {
  const v: ClassInput = entry ?? {
    name: c.name,
    alsoAccepts: c.alsoAccepts.join(", "),
    nec11016Label: c.nec11016Label,
    ieeeClass: c.ieeeClass,
    card: c.card,
    devices: c.devices.join(", "),
    rating: c.rating,
    positionType: c.positionType,
    notes: c.notes,
  };
  const input = (name: keyof ClassInput, label: string, hint = "") =>
    `<label class="r"><span class="k">${esc(label)}</span><span class="v"><input class="fin wide" name="${name}" value="${esc(v[name])}" autocomplete="off">${hint ? `<small class="fhint">${esc(hint)}</small>` : ""}</span><span class="e"></span></label>`;
  return `<a class="back" href="#/lists/classes/${encodeURIComponent(c.code)}">Cancel</a>
    ${ident(c.code.slice(0, 3), "Change Class", `${c.name} · ${c.code}`)}
    <form class="box" id="class-form" data-code="${esc(c.code)}" novalidate>
      <div class="rows">
        ${row("Code", `<span class="chip">${esc(c.code)}</span>`, '<span class="lock">Locked</span>')}
        ${input("name", "Name")}
        <label class="r"><span class="k">NEC 110.16 Label</span><span class="v"><span class="tick inline"><input type="checkbox" name="nec11016Label"${v.nec11016Label ? " checked" : ""}><span class="t"><b>Needs the label</b></span></span></span><span class="e"></span></label>
        ${input("ieeeClass", "IEEE Class")}
        ${input("card", "Card")}
        ${input("rating", "Rating")}
        ${input("devices", "Devices", "Separate with commas, like Breaker, Fuse")}
        ${input("positionType", "Position Type")}
        ${input("alsoAccepts", "Also Accepts", "Other codes that mean this class, separated with commas")}
        ${input("notes", "Notes")}
      </div>
    </form>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="prim" type="submit" form="class-form">Save</button>`;
}

export function classSaveSheet(c: EquipmentClass, changes: string[], busy: boolean, error = ""): string {
  return sheet(
    `Save changes to ${c.name}?`,
    `<p>${changes.map(esc).join(" ")} The IEA Site Survey uses this the next time it loads, at every site. Assets already walked keep what they saved.</p>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="go" type="button" data-act="class-save-confirm"${busy ? " disabled" : ""}>${busy ? "Saving…" : "Save"}</button>
    <button class="go2" type="button" data-act="sheet-cancel">Cancel</button>`,
  );
}

export function itemActiveSheet(name: string, kind: "class" | "value", isOn: boolean, busy: boolean, error = ""): string {
  const what = kind === "class" ? "class" : "value";
  return sheet(
    `${isOn ? "Turn on" : "Turn off"} ${name}?`,
    `<p>${
      isOn
        ? `${esc(name)} shows as a choice in the IEA Site Survey again the next time it loads, at every site.`
        : `${esc(name)} stops showing as a choice in the IEA Site Survey the next time it loads, at every site. Assets already set to this ${what} keep it.`
    }</p>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="go${isOn ? "" : " red"}" type="button" data-act="item-active-confirm"${busy ? " disabled" : ""}>${busy ? "Saving…" : isOn ? "Turn On" : "Turn Off"}</button>
    <button class="go2" type="button" data-act="sheet-cancel">Cancel</button>`,
  );
}

// ---- 5 to 8. Field lists ----

export function fieldListsIndex(d: ListsData): string {
  const lists = listSummaries(d.values, FIELD_LIST_ORDER);
  return `<a class="back" href="#/lists">Lists</a>
    ${ident("FL", `Field Lists · ${lists.length}`, "The dropdowns the IEA Site Survey uses")}
    <div class="box">${lists
      .map(
        (l) => `<a class="menu-row" href="#/lists/fields/${slug(l.listName)}"><span class="t"><b>${esc(l.listName)}</b>${l.active < l.count ? `<small>${l.count - l.active} off</small>` : ""}</span><span class="count">${l.count}</span><span class="chev">›</span></a>`,
      )
      .join("")}</div>`;
}

export function listNameFromSlug(d: ListsData, s: string): string | null {
  return listSummaries(d.values).find((l) => slug(l.listName) === s)?.listName ?? null;
}

export function fieldList(d: ListsData, listName: string, flash = "", order?: ListOrderView): string {
  const s = slug(listName);
  const groups = groupValues(d.values, listName).map((g) => ({ ...g, values: inOrder(g.values, (v) => v.id, order?.ids[g.group]) }));
  const all = groups.flatMap((g) => g.values);
  const count = all.length;
  const body = groups
    .map(
      (g) => `${g.group ? `<div class="grp"><span>${esc(g.group)}</span><span>${g.values.length}</span></div>` : ""}
      <div class="box" data-group="${esc(g.group)}">${g.values
        .map((v) => {
          const body = `<b>${esc(v.value)}</b>${v.notes ? `<small>${esc(v.notes)}</small>` : ""}`;
          return order
            ? `<div class="val-row${v.isActive ? "" : " dim"}" data-drag-item="${esc(v.id)}">${handle(v.value)}<span class="t">${body}</span>${v.isActive ? "" : '<span class="pill grey">OFF</span>'}</div>`
            : `<div class="val-row${v.isActive ? "" : " dim"}">
            <a class="t" href="#/lists/fields/${s}/v/${encodeURIComponent(v.id)}">${body}</a>
            ${v.isActive ? "" : '<span class="pill grey">OFF</span>'}
          </div>`;
        })
        .join("")}</div>`,
    )
    .join("");
  return `<a class="back" href="#/lists/fields">Field Lists</a>
    ${ident("FL", `${listName} · ${count}`, `Tap a value to edit or switch it off. Order changes the order${groups.length > 1 ? " inside its group" : ""}.`)}
    ${order ? "" : flash}
    ${
      listName === PREFIX_LIST && !order
        ? `<div class="note">Tag letters for each SKM type. Change the letters and every tag of that type reads the new letters. The numbers never change. A new type needs a build.</div>${count > 1 ? `<div class="pb-actions">${orderButtons(undefined)}</div>` : ""}`
        : order
          ? `<div class="pb-actions">${orderButtons(order)}</div>${orderLines(order)}`
          : `<div class="pb-actions"><a class="chg" href="#/lists/fields/${s}/new">Add Value</a>${count > 1 ? orderButtons(undefined) : ""}</div>`
    }
    <div class="vals">${body || '<div class="box"><div class="empty">No values yet.</div></div>'}</div>`;
}

export function fieldValueForm(d: ListsData, listName: string, v: FieldValue | null, entry: FieldValueInput | null, busy: boolean, error = ""): string {
  const s = slug(listName);
  const e: FieldValueInput = entry ?? { value: v?.value ?? "", group: v?.group ?? "", notes: v?.notes ?? "", isActive: v?.isActive ?? true };
  const groups = groupsOf(d.values, listName);
  return `<a class="back" href="#/lists/fields/${s}">Cancel</a>
    ${ident("FL", v ? v.value : "Add Value", `Field list · ${listName}`)}
    <form class="box" id="value-form" data-list="${esc(listName)}" data-id="${esc(v?.id ?? "")}" novalidate>
      <div class="rows">
        <label class="r"><span class="k">Value</span><span class="v"><input class="fin wide" name="value" value="${esc(e.value)}" maxlength="80" autocomplete="off" required></span><span class="e"></span></label>
        ${
          groups.length || e.group
            ? listName === PREFIX_LIST
              ? `<label class="r"><span class="k">SKM Type</span><span class="v"><input class="fin wide" name="group" value="${esc(e.group)}" readonly><small class="fhint">Fixed. Only the letters change.</small></span><span class="e"></span></label>`
              : `<label class="r"><span class="k">Group</span><span class="v"><input class="fin wide" name="group" value="${esc(e.group)}" list="value-groups" autocomplete="off"><datalist id="value-groups">${groups.map((g) => `<option value="${esc(g)}">`).join("")}</datalist><small class="fhint">${esc(groups.join(", "))}</small></span><span class="e"></span></label>`
            : '<input type="hidden" name="group" value="">'
        }
        <label class="r"><span class="k">Notes (optional)</span><span class="v"><input class="fin wide" name="notes" value="${esc(e.notes)}" placeholder="Why it was added" maxlength="300" autocomplete="off"></span><span class="e"></span></label>
        <label class="r"><span class="k">Active</span><span class="v"><span class="tick inline"><input type="checkbox" name="isActive"${e.isActive ? " checked" : ""}><span class="t"><b>Active</b><small>Off hides it as a choice. Assets already set to it keep it.</small></span></span></span><span class="e"></span></label>
      </div>
    </form>
    ${v ? "" : '<div class="note">Added at the end of its group. Use Up and Down to put it in place after saving.</div>'}
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="prim" type="submit" form="value-form"${busy ? " disabled" : ""}>${busy ? "Saving…" : "Save"}</button>`;
}

// ---- 9 and 10. Plants ----

/** sites: the live site numbers. A plant that became a site leaves this list (Add Site, 2026-10-23). */
export function plantList(d: ListsData, flash = "", sites: readonly string[] = []): string {
  const plants = d.plants.filter((p) => !sites.includes(p.siteNumber));
  const rows = sortPlants(plants)
    .map(
      (p) => `<a class="menu-row" href="#/lists/plants/${esc(p.siteNumber)}"><span class="num">${esc(p.siteNumber)}</span>
        <span class="t"><b>${esc(p.name)}</b><small>${esc([p.city, p.state].filter(Boolean).join(", "))}</small></span><span class="chev">›</span></a>`,
    )
    .join("");
  return `<a class="back" href="#/lists">Lists</a>
    ${ident("PL", `Plants · ${plants.length}`, "Every plant that is not a site yet")}
    ${flash}
    <div class="pb-actions"><a class="chg" href="#/lists/plants/new">Add Plant</a></div>
    <div class="box">${rows || '<div class="empty">No plants.</div>'}</div>`;
}

export function plantForm(p: Plant | null, entry: PlantInput | null, busy: boolean, error = ""): string {
  const v: PlantInput = entry ?? { siteNumber: p?.siteNumber ?? "", name: p?.name ?? "", city: p?.city ?? "", state: p?.state ?? "" };
  return `<a class="back" href="#/lists/plants">Cancel</a>
    ${ident(p ? p.siteNumber : "+", p ? p.name : "Add Plant", p ? `Plant ${p.siteNumber}` : "A plant that is not a site yet")}
    <form class="box" id="plant-form" data-old="${esc(p?.siteNumber ?? "")}" novalidate>
      <div class="rows">
        <label class="r"><span class="k">Number</span><span class="v"><input class="fin num" name="siteNumber" value="${esc(v.siteNumber)}" inputmode="numeric" maxlength="2" autocomplete="off" required><small class="fhint">Two digits. Not 00, 01, 02, 04 or 10; those are sites.</small></span><span class="e"></span></label>
        <label class="r"><span class="k">Name</span><span class="v"><input class="fin wide" name="name" value="${esc(v.name)}" maxlength="60" autocomplete="off" required></span><span class="e"></span></label>
        <label class="r"><span class="k">City</span><span class="v"><input class="fin wide" name="city" value="${esc(v.city)}" maxlength="60" autocomplete="off"></span><span class="e"></span></label>
        <label class="r"><span class="k">State</span><span class="v"><input class="fin num" name="state" value="${esc(v.state)}" maxlength="2" autocapitalize="characters" autocomplete="off"></span><span class="e"></span></label>
      </div>
    </form>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <div class="pb-actions"><button class="prim" type="submit" form="plant-form"${busy ? " disabled" : ""}>${busy ? "Saving…" : "Save"}</button>
    ${p ? '<button class="prim danger" type="button" data-act="plant-delete">Remove Plant</button>' : ""}</div>`;
}

export function plantDeleteSheet(p: Plant, busy: boolean, error = ""): string {
  return sheet(
    `Remove ${p.name}?`,
    `<p>Plant ${esc(p.siteNumber)} comes off the list. Nothing else is touched.</p>
    ${error ? `<div class="note red">${esc(error)}</div>` : ""}
    <button class="go red" type="button" data-act="plant-delete-confirm"${busy ? " disabled" : ""}>${busy ? "Removing…" : "Remove Plant"}</button>
    <button class="go2" type="button" data-act="sheet-cancel">Keep</button>`,
  );
}
